// main.js – boot Firebird in a Worker, load the PAK into it, run the loop.
//
// Per frame the browser does a handful of queries:
//   SELECT * FROM quake_tic(...)      – advance the game by N tics
//   SELECT * FROM frame_faces         – the polygons on screen
//   SELECT * FROM frame_ents          – the models in view
//   SELECT * FROM frame_lightstyles   – this frame's light animation
//   SELECT ... FROM sound_events / fx_events
// and then paints. All game state lives in Firebird tables.

import { FirebirdBrowser } from 'firebird-wasm/browser';
import schemaSql from '../sql/schema.sql';
import physicsSql from '../sql/physics.sql';
import gameSql from '../sql/game.sql';
import moversSql from '../sql/movers.sql';
import triggersSql from '../sql/triggers.sql';
import itemsSql from '../sql/items.sql';
import combatSql from '../sql/combat.sql';
import spawnSql from '../sql/spawn.sql';
import weaponsSql from '../sql/weapons.sql';
import monstersSql from '../sql/monsters.sql';
import renderSql from '../sql/render.sql';
import qcvmSql from '../sql/qcvm.sql';
import qcbuiltinsSql from '../sql/qcbuiltins.sql';
import qcexecSql from '../sql/qcexec.sql';
import qcserverSql from '../sql/qcserver.sql';
import qcphysicsSql from '../sql/qcphysics.sql';
import qcpageSql from '../sql/qcpage.sql';
import saveSql from '../sql/save.sql';
import demoSql from '../sql/demo.sql';
import botsSql from '../sql/bots.sql';
import hostSql from '../sql/host.sql';
import { Pak, Wad2, loadPalette, PakSet, qpic } from './pak.js';
import { createSchema, loadResources, loadMap, loadProgs, setView } from './loader.js';
import { QcJit } from './qcjit.js';
import { exportSave, importSave, saveFile, readSaveFile, SaveStore } from './saves.js';
import { exportDemo, importDemo, DemoPlayer } from './demos.js';
import { frameDlights, dlightAt } from './dlights.js';
import { Menu } from './menu.js';
import { Renderer, lightPoint } from './renderer.js';
import { modFromZip } from './zip.js';
import { Bindings, keyName, MOUSE_NAMES, BUTTONS } from './binds.js';
import { Console } from './console.js';
import { TouchControls, attachTouch } from './touch.js';
import { Hud, VIEW_MODELS } from './hud.js';
import { QuakeAudio } from './audio.js';

const $ = (id) => document.getElementById(id);
const canvas = $('screen');
const statusEl = $('status');
const statsEl = $('stats');
const TIC_MS = 50;

let db, pak, res, renderer, hud, wad;
let map = null;          // { name, bsp }
let last = null;         // last QUAKE_TIC row
let running = false;
let paused = false;
let lastTic = 0;
let lastSoundId = 0;
let lastFxId = 0;
let finaleShown = false;
let menu = null;           // Quake's menu (src/menu.js): up, the game pauses
let interWait = 0;         // the PSQL game's intermission: when it began (the stats stay until fire)
let finaleStart = 0;       // game time the finale's text began (QuakeC's svc_finale)
let cdTrack = -1;          // the track svc_cdtrack asked for
let lastDraw = null;       // the last frame's rows, drawn again under the intermission
let muzzleUntil = 0;       // the player's muzzle flash lights the room until then (game time)
let prevWeaponFrame = 0;
let beams = [];          // lightning beams to draw briefly
let playerEnt = 1;       // the player's ents row: its own beam follows it (cl_tent.c)
let explosions = [];
const settings = { map: 'start', detail: 'high', sfx: 70, music: 50, musicMode: 'tracks', skill: 1, fov: 90, renderer: 'fast', data: 'shareware', logic: 'psql',
  sensitivity: 3, alwaysRun: true, invertMouse: false, mode: 'single', bots: 3, fraglimit: 10, liquids: 'opaque',
  alphas: { water: 0.5, lava: 0, slime: 0, tele: 0 } };   // r_wateralpha (when Liquids is translucent), r_lavaalpha, r_slimealpha, r_telealpha
let progsLoaded = false;   // the pak's progs.dat in the QuakeC VM's tables (QuakeC mode)
let jit = null;            // its hot functions compiled to PSQL procedures (src/qcjit.js)
let jitTics = 0;
let pakKey = '';           // the paks in use: saves are kept per data set (model ids depend on the paks)
let base = null;           // the data set's paks as loaded ({ buffers, label }), for laying a mod over them
let mod = null;            // the mod laid over them ({ name, paks, progs }, src/zip.js), or null
let saveSlots = new Array(12).fill(null);   // the menu's slot names (SaveGame_Comment), from the browser's store
const QUICK_SLOT = 12;     // F6 / F9: Quake's quick.sav, not in the menu's list
let recording = false;     // a demo is being recorded (sql/demo.sql)
let demoPlayer = null;     // a demo playing back: its recorded calls replace the input (src/demos.js)
let demo = null;           // the last demo recorded or opened
let scores = [];           // deathmatch: every client's frags (qc_scores), refreshed a few times a second
let scoresAt = 0;
try { Object.assign(settings, JSON.parse(localStorage.getItem('firebird-quake:settings') || '{}')); } catch { /* defaults */ }
const saveSettings = () => { try { localStorage.setItem('firebird-quake:settings', JSON.stringify(settings)); } catch { /* ignore */ } };
// the screen: 640×400 ('max'), 320×200 ('high', Quake's own) or 160×100 ('low'), scaled by CSS; above 320 the
// status bar stays 320×24 and centred, as Quake draws it in higher video modes
const DETAIL = { max: [640, 400, 24], high: [320, 200, 24], low: [160, 100, 12] };
const detailOf = () => DETAIL[settings.detail] ?? DETAIL.high;
const viewWidth = () => detailOf()[0];
const viewHeight = () => detailOf()[1];
const sbarLines = () => detailOf()[2];   // the 3D view is the part above the status bar
const audio = new QuakeAudio();
audio.setVolume(settings.sfx / 100);
audio.setMusicVolume(settings.music / 100);
audio.musicMode = settings.musicMode;
for (const ev of ['keydown', 'pointerdown', 'touchstart']) window.addEventListener(ev, () => audio.unlock(), { capture: true });
document.addEventListener('visibilitychange', () => audio.suspend(document.hidden));
const perf = { tic: 0, faces: 0, ents: 0, draw: 0, rows: 0, nfaces: 0 };

function setStatus(msg, isError = false) {
  statusEl.textContent = msg;
  statusEl.classList.toggle('error', isError);
  statusEl.hidden = !msg;
}

// ── input ────────────────────────────────────────────────────────────────
// Every key, mouse button and wheel turn is a binding (src/binds.js): a + button is held while its key
// is down and read into the tic's input; any other command runs on the console when the key goes down
let binds = new Bindings();
try { const saved = JSON.parse(localStorage.getItem('firebird-quake:binds') || 'null'); if (saved) binds = new Bindings(saved); } catch { /* the defaults */ }
const saveBinds = () => { try { localStorage.setItem('firebird-quake:binds', JSON.stringify(binds)); } catch { /* ignore */ } };
let mouseYaw = 0, mousePitch = 0;
let impulse = 0;
// the console's aliases are kept as the bindings are; exec reads its files from the paks (a mod's first)
let savedAliases = null;
try { savedAliases = JSON.parse(localStorage.getItem('firebird-quake:aliases') || 'null'); } catch { /* none */ }
const con = new Console({
  commands: consoleCommands(),
  aliases: savedAliases,
  onAliases: (a) => { try { localStorage.setItem('firebird-quake:aliases', JSON.stringify(a)); } catch { /* ignore */ } },
  readFile: (name) => (pak?.has(name) ? new TextDecoder('latin1').decode(pak.get(name)) : null),
  nextFrame: () => new Promise((resolve) => requestAnimationFrame(() => resolve())),
});
con.print('Firebird Quake. The console: help lists the commands, ` puts it away.', false);
// a key: its binding runs, or holds its button; a binding to a +alias runs the alias on the key going down
// and its -alias on the key coming up, as keys.c does for any + command
const press = (name, down) => {
  const b = name ? binds.get(name) : null;
  if (b?.startsWith('+') && con.aliases.has(b.toLowerCase())) {
    if (down) con.execute(b, false);
    else if (con.aliases.has(`-${b.slice(1).toLowerCase()}`)) con.execute(`-${b.slice(1)}`, false);
    return;
  }
  const cmd = binds.key(name, down);
  if (cmd) con.execute(cmd, false);
};
window.addEventListener('keydown', (e) => {
  if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLSelectElement) return;
  if (!running) return;
  // the menu takes every key while it is up; Escape brings it up (and lets the mouse go)
  if (menu?.active) {
    if (e.code === 'Escape' && performance.now() - menuOpened < 400) { e.preventDefault(); return; }   // the Escape that released the mouse
    if (menu.key(e.code)) e.preventDefault();
    if (!menu.active && running) canvas.requestPointerLock?.()?.catch?.(() => {});
    return;
  }
  // the console takes every key while it is down (` and Escape put it away)
  if (con.active) { if (con.key(e.code, e.key)) e.preventDefault(); return; }
  if (e.code === 'Escape') { e.preventDefault(); openMenu(); document.exitPointerLock?.(); return; }
  const name = keyName(e.code);
  if (name && binds.get(name)) e.preventDefault();
  if (!e.repeat) press(name, true);
});
window.addEventListener('keyup', (e) => press(keyName(e.code), false));
let menuOpened = 0;
function openMenu() { if (!menu || menu.active) return; con.toggle(false); menu.open(); menuOpened = performance.now(); binds.release(); touch.release(); }
// the browser's Escape releases the mouse before the page hears the key: bring the menu up then, as Quake's Escape does
document.addEventListener('pointerlockchange', () => { if (document.pointerLockElement !== canvas && running && !paused && !interWait && !con.active) openMenu(); });
window.addEventListener('blur', () => { binds.release(); touch.release(); });
canvas.addEventListener('click', () => {
  if (running && document.pointerLockElement !== canvas) canvas.requestPointerLock?.()?.catch?.(() => {});
});
canvas.addEventListener('mousedown', (e) => { if (document.pointerLockElement === canvas && MOUSE_NAMES[e.button]) press(MOUSE_NAMES[e.button], true); });
window.addEventListener('mouseup', (e) => { if (MOUSE_NAMES[e.button]) press(MOUSE_NAMES[e.button], false); });
window.addEventListener('mousemove', (e) => {
  if (document.pointerLockElement === canvas) {
    mouseYaw -= e.movementX * 0.05 * settings.sensitivity;
    mousePitch += e.movementY * 0.05 * settings.sensitivity * (settings.invertMouse ? -1 : 1);
  }
});
window.addEventListener('wheel', (e) => {
  if (document.pointerLockElement !== canvas || !e.deltaY) return;
  const name = e.deltaY < 0 ? 'mwheelup' : 'mwheeldown';
  press(name, true); press(name, false);
});
// touch (src/touch.js): a stick where the left thumb lands, the right side looks (a tap fires), buttons
// for fire, jump, the next weapon and the menu, and a pad that drives the menu
const touch = new TouchControls();
attachTouch(canvas.parentElement, canvas, touch, {
  run: (cmd) => con.execute(cmd, false),
  menuKey: (code) => { if (menu?.active) menu.key(code); },
  openMenu: () => openMenu(),
  menuActive: () => menu?.active,
});

function readInput(tics) {
  const b = (button) => (binds.down(button) ? 1 : 0);
  let fwd = b('+forward') - b('+back');
  let side = b('+moveright') - b('+moveleft');
  const turnKeys = b('+left') - b('+right');
  const lookKeys = b('+lookdown') - b('+lookup');
  const run = (settings.alwaysRun ? !b('+speed') : b('+speed')) ? 1 : 0;     // always run (+speed walks), or +speed runs
  const stick = touch.axes(), turned = touch.take();
  if (stick.knob) { fwd = stick.fwd; side = stick.side; }
  const yaw = turnKeys * 7 * tics + mouseYaw + turned.yaw * settings.sensitivity / 3;
  const pitch = lookKeys * 5 * tics + mousePitch + turned.pitch * settings.sensitivity / 3 * (settings.invertMouse ? -1 : 1);
  mouseYaw = 0; mousePitch = 0;
  const fire = b('+attack') || turned.buttons.has('+attack') || turned.taps ? 1 : 0;
  const jump = b('+jump') || b('+moveup') || turned.buttons.has('+jump') ? 1 : 0;
  const imp = impulse;
  impulse = 0;
  return [tics, fwd, side, yaw, pitch, fire, jump, run, imp];
}

// ── maps ─────────────────────────────────────────────────────────────────
// save: an exported save game (src/saves.js) to put back on the freshly loaded map instead of a new spawn;
// seed: the level's random seed (a demo's), else init_map picks one
// Level loads one at a time: a control that loads a level (the mode, the map, a mod, the data set) may be
// changed while another load is running, and two loads at once clear each other's tables
let loading = Promise.resolve();
function serial(fn) {
  const p = loading.then(fn, fn);
  loading = p.catch(() => {});
  return p;
}
function startMap(...args) { return serial(() => loadLevel(...args)); }
async function loadLevel(name, newGame, { save = null, seed = null } = {}) {
  running = false;
  await drain();                          // a tic in flight was the old level's
  if (recording) await stopRecording();   // a demo is one level
  if (!seed) demoPlayer = null;
  setStatus(`Loading ${name} into Firebird…`);
  const t0 = performance.now();
  // QuakeC mode: the client's parms leave with it (SetChangeParms), the level is spawned by progs.dat's own spawn
  // functions after the geometry is loaded, and the parms come back (sql/qcpage.sql: qc_change_parms, qc_begin_map)
  const qc = settings.logic === 'qc';
  if (qc && !newGame && progsLoaded) await db.exec('EXECUTE PROCEDURE qc_change_parms');
  if (!qc) await db.exec('EXECUTE PROCEDURE qc_leave');
  const bsp = await loadMap(db, pak, res, name, { skill: settings.skill, newGame, seed });
  if (qc) {
    if (!progsLoaded) {
      setStatus('Loading progs.dat into the QuakeC VM…');
      jit = new QcJit(db, await loadProgs(db, pak));
      await jit.init();
      progsLoaded = true;
    }
    if (save) await db.exec('EXECUTE PROCEDURE qc_enter');   // the save's edicts replace the spawn
    else {
      // the server's rules (sql/bots.sql): deathmatch or coop, the bots as clients 2..
      const dm = settings.mode === 'deathmatch' ? 1 : 0, coop = settings.mode === 'coop' ? 1 : 0;
      await db.exec(`EXECUTE PROCEDURE qc_setup_server(${dm}, ${coop}, ${dm || coop ? settings.bots : 0}, ${dm ? settings.fraglimit : 0}, 0)`);
      // a mod's own bots (FrikBot's impulse 100) take client slots the player and our bots leave free
      if (mod?.progs && (dm || coop)) await db.exec('EXECUTE PROCEDURE qc_set_maxclients(8)');
      setStatus(`Spawning ${name} through QuakeC…`);
      await db.exec(`EXECUTE PROCEDURE qc_begin_map(${settings.skill}, ${newGame ? 0 : 1})`);
    }
    // the functions the spawn ran more than once, compiled to procedures (the rest follow as they get hot)
    setStatus('Compiling QuakeC to PSQL…');
    await jit.compileHot({ min: 2, max: 32 });
  }
  if (save) {
    setStatus(`Loading the saved game…`);
    await importSave(db, save);
    await db.exec(`EXECUTE PROCEDURE load_game(${save.slot})`);
  }
  map = { name, bsp };
  renderer.setResources(res);
  renderer.skyTex = bsp.textures.find((t) => t && t.name.startsWith('sky')) ?? null;
  renderer.particles = [];
  beams = []; explosions = [];
  interWait = 0; finaleStart = 0; cdTrack = -1; lastDraw = null; muzzleUntil = 0; prevWeaponFrame = 0; scores = []; scoresAt = 0;
  audio.setAmbients(bsp.entities);
  const world = bsp.entities.find((e) => e.classname === 'worldspawn');
  renderer.setMapAlphas(world);         // the map's own wateralpha, lavaalpha… (LibreQuake sets them)
  audio.playMusic(Number(world?.sounds ?? 0));
  const { rows } = await db.query('SELECT MAX(id) m FROM sound_events');
  lastSoundId = rows[0].M ?? 0;
  lastFxId = 0;
  playerEnt = (await db.query('SELECT ent_id e FROM player')).rows[0]?.E ?? 1;
  console.log(`[firebird-quake] ${name} loaded in ${(performance.now() - t0).toFixed(0)} ms`);
  setStatus('');
  $('mapname').textContent = name;
  $('map').value = name;
  lastTic = performance.now();
  running = true;
}

// ── save games (sql/save.sql, src/saves.js) ───────────────────────────────
// Host_Savegame_f: save_game copies the state's rows into the slot, and the browser keeps them
async function saveGame(slot) {
  if (!running || !db) return;
  try {
    await db.exec(`EXECUTE PROCEDURE save_game(${slot})`);
    const save = await exportSave(db, slot);
    if (SaveStore.available()) await SaveStore.put(pakKey, slot, save);
    if (slot < 12) saveSlots[slot] = save.meta.COMMENT;
    lastSave = save; $('save-download').disabled = false;
    flash(`Saving game to s${slot === QUICK_SLOT ? 'quick' : slot}.sav... ${save.meta.COMMENT.replace(/ +/g, ' ')}`);
  } catch (err) {
    flash(sqlMessage(err), true);
  }
}

// Host_Loadgame_f: the save's map loaded under the save's logic, then load_game puts the rows back
async function loadGame(slot) {
  if (!db || !pak) return;
  try {
    const save = SaveStore.available() ? await SaveStore.get(pakKey, slot) : null;
    if (!save) { flash('no saved game in that slot', true); return; }
    await loadSave(save);
  } catch (err) {
    console.error(err);
    setStatus(sqlMessage(err), true);
  }
}
// a save, from a slot or a file: its map under its logic and skill, its rows put back, its rules
async function loadSave(save) {
  lastSave = save; $('save-download').disabled = false;
  const logic = save.meta.QC_MODE === 1 ? 'qc' : 'psql';
  if (logic !== settings.logic) { settings.logic = logic; $('logic').value = logic; }
  settings.skill = save.meta.SKILL; $('skill').value = String(save.meta.SKILL); saveSettings();
  await startMap(save.meta.MAP_NAME, true, { save });
  // a deathmatch or coop save brings its rules back with the game row
  const g = (await db.query('SELECT deathmatch, coop, maxclients FROM game WHERE id = 1')).rows[0];
  setMode(g.DEATHMATCH ? 'deathmatch' : g.COOP ? 'coop' : 'single', false);
  if (g.MAXCLIENTS > 1) { settings.bots = g.MAXCLIENTS - 1; $('bots').value = String(settings.bots); saveSettings(); }
}
let lastSave = null;       // the save last made or loaded: the one the Download button gives

const refreshSaves = async () => { try { saveSlots = SaveStore.available() ? await SaveStore.list(pakKey) : new Array(12).fill(null); } catch { saveSlots = new Array(12).fill(null); } };
// the line Firebird's exception carries (save_error's text), not the whole report
const sqlMessage = (err) => {
  const lines = String(err.message).split('\n').map((l) => l.replace(/^-/, ''));
  const i = lines.findIndex((l) => l.includes('SAVE_ERROR'));   // -"PUBLIC"."SAVE_ERROR", then its text
  return i >= 0 && lines[i + 1] ? lines[i + 1] : err.message;
};
let flashTimer = 0;
function flash(msg, isError = false) {
  setStatus(msg, isError);
  clearTimeout(flashTimer);
  flashTimer = setTimeout(() => { if (running) setStatus(''); }, 2500);
}

// ── demos (sql/demo.sql, src/demos.js) ─────────────────────────────────────
// Record: the level again as a new game, then every quake_tic call's arguments; Play: the level with the
// demo's seed, skill and logic, fed the recorded calls. The last demo is kept in the browser and can be
// saved as a file.
// cl_demo.c's record: from this moment (a demo recorded mid-level carries a save of it, sql/demo.sql)
async function startRecording() {
  if (!map || demoPlayer) return;
  await serial(async () => {
    // (the engine runs calls in order: a tic sent before this is in the save, every one after is recorded)
    try { await db.exec('EXECUTE PROCEDURE demo_record'); } catch (err) { flash(sqlMessage(err), true); return; }
    recording = true;
    updateDemoButtons();
    flash(`recording a demo of ${map.name}`);
  });
}

async function stopRecording() {
  recording = false;
  await db.exec('EXECUTE PROCEDURE demo_stop');
  demo = await exportDemo(db);
  if (demo && SaveStore.available()) await SaveStore.put(pakKey, 'demo', demo).catch(() => {});
  updateDemoButtons();
  if (demo) flash(`demo of ${demo.map} recorded: ${demo.tics.reduce((n, r) => n + r[0], 0)} tics`);
}

async function playDemo(d = demo) {
  if (!d || !db) return;
  try {
    if (!pak.has(`maps/${d.map}.bsp`)) throw new Error(`${d.map} is not in this pak`);
    if (recording) await stopRecording();
    await importDemo(db, d);
    settings.logic = d.qc === 1 ? 'qc' : 'psql'; $('logic').value = settings.logic;
    setMode(d.deathmatch ? 'deathmatch' : d.coop ? 'coop' : 'single', false);
    if (d.nbots) { settings.bots = d.nbots; $('bots').value = String(d.nbots); }
    if (d.fraglimit) settings.fraglimit = d.fraglimit;
    settings.skill = d.skill; $('skill').value = String(d.skill); saveSettings();
    await startMap(d.map, true, d.save ? { save: d.save } : { seed: d.seed });   // recorded mid-level: from its save
    demoPlayer = new DemoPlayer(d);
    updateDemoButtons();
  } catch (err) {
    setStatus(err.message, true);
  }
}

function updateDemoButtons() {
  $('demo-record').textContent = recording ? 'Stop recording' : 'Record demo';
  $('demo-play').disabled = !demo || recording;
  $('demo-save').disabled = !demo;
}

// ── the loop ─────────────────────────────────────────────────────────────
// The game runs in real time: a tic (0.05 s of game) is issued when the wall clock owes one, twenty a
// second, up to four at once when the page fell behind. The engine runs in its own worker, so a tic and
// its frame queries run there while the main thread paints: `pending` is the result in flight, issued as
// soon as one is owed and painted when it arrives; between tics the last frame is drawn again, so the
// particles, the light styles, the beams and the weapon's bob move at the display's rate. A level change
// drains the tic in flight first (drain), and a result from before a level change is dropped.
let pending = null;
let mapGen = 0;
let lastMsg = null;
let ticAt = 0;             // when the frame's tic result arrived (the time-driven effects run on from it)
let drawAt = 0;            // when the last frame was drawn (the particles' wall-clock step)
let ticCount = 0;          // tics this second, for the stats line
async function drain() {
  if (pending) { try { await pending; } catch { /* its error was the old level's */ } pending = null; }
  mapGen++;
}
function nextFrame() {
  let done = false;
  const go = () => { if (!done) { done = true; frame(); } };
  requestAnimationFrame(go);
  setTimeout(go, 60);
}

const arr = { rowMode: 'array' };

// the tic (or a demo's recorded calls) and the frame's queries, as one promise: how many tics the wall
// clock owes, the input for them, then everything the frame needs from the engine
function issue() {
  const now = performance.now();
  const tics = Math.min(4, Math.floor((now - lastTic) / TIC_MS));
  if (tics < 1) return null;                 // no tic owed yet
  lastTic += tics * TIC_MS;
  if (now - lastTic > 200) lastTic = now;    // far behind (a hidden tab): no chase
  // the PSQL game, or progs.dat in the QuakeC VM: the same input, the same row; a demo's recorded calls
  // when one is playing (the keyboard and mouse are read and dropped)
  const input = readInput(tics);
  const calls = demoPlayer ? demoPlayer.take(tics) : [input];
  return compute(tics, calls, !!demoPlayer?.done, lastSoundId, lastFxId);
}
async function compute(tics, calls, demoDone, soundFrom, fxFrom) {
  const t0 = performance.now();
  const ticSql = `SELECT * FROM ${settings.logic === 'qc' ? 'qc_tic' : 'quake_tic'}(?, ?, ?, ?, ?, ?, ?, ?, ?)`;
  let row = null;
  for (const args of calls) row = (await db.query(ticSql, args, { rowMode: 'object' })).rows[0];
  if (settings.logic === 'qc' && settings.mode !== 'single' && performance.now() - scoresAt > 300) {
    scoresAt = performance.now();
    scores = (await db.query('SELECT c, name, frags, alive FROM qc_scores', [], arr)).rows;
  }
  // QuakeC mode: about once a second, the hottest few functions still interpreted get compiled (more under a
  // mod, whose code nothing has compiled before: FrikBot X's bots run its whole client physics in QuakeC)
  if (settings.logic === 'qc' && jit && (jitTics += tics) >= 20) { jitTics = 0; await jit.compileHot({ min: 3, max: mod?.progs ? 10 : 3 }); }
  const ticMs = performance.now() - t0;
  const t1 = performance.now();
  const q = (sql) => db.query(sql, [], arr).then((r) => r.rows);
  const [faces, ents, styles, sounds, fx, bframes] = await Promise.all([
    q(settings.renderer === 'sql' ? 'SELECT * FROM frame_faces' : 'SELECT * FROM frame_faces_fast'), q('SELECT * FROM frame_ents'), q('SELECT * FROM frame_lightstyles'),
    q(`SELECT id, tic, ent_id, chan, snd, vol, attn, x, y, z FROM sound_events WHERE id > ${soundFrom} ORDER BY id`),
    q(`SELECT id, kind, x, y, z, x2, y2, z2, n FROM fx_events WHERE id > ${fxFrom} ORDER BY id`),
    q("SELECT e.id, e.frame FROM ents e JOIN models m ON m.id = e.model_id WHERE m.kind = 'B' AND e.frame <> 0"),
  ]);
  return { last: row, tics, faces, ents, styles, sounds, fx, bframes, demoDone, ticMs, facesMs: performance.now() - t1 };
}

async function frame() {
  if (!running || paused || document.hidden || menu?.active) {
    lastTic = performance.now();
    // the game waits: the last frame again, under the menu (dimmed) or Quake's PAUSE plaque
    if ((paused || menu?.active) && renderer && last && !document.hidden) {
      drawFrame(lastDraw?.faces ?? null, lastDraw?.ents ?? [], lastDraw?.styles ?? new Float32Array(64), last.TIME_, 0, (r) => {
        if (menu?.active) { r.fadeScreen(); menu.draw(r, performance.now() / 1000); return; }
        const p = menu?.pic('gfx/pause.lmp');
        if (p) r.drawPic(p, (r.w - p.w) >> 1, (r.h - 48 - p.h) >> 1); else hud.drawCenter(r, 'paused', 80);
      });
    }
    nextFrame();
    return;
  }
  try {
    // the PSQL game's intermission: no more tics; the last frame and the stats, until fire after two seconds
    if (interWait) {
      lastTic = performance.now();
      const [, , , , , fire, jump] = readInput(1);
      if (lastDraw) drawFrame(lastDraw.faces, lastDraw.ents, lastDraw.styles, last.TIME_, 0);
      if ((fire || jump) && performance.now() - interWait > 2000) { interWait = 0; await nextLevel(); }
      nextFrame();
      return;
    }

    let cur = null;
    if (pending) {
      const gen = mapGen;
      cur = await pending;
      pending = null;
      if (gen !== mapGen || !running) { nextFrame(); return; }  // a level loaded meanwhile: the result was its predecessor's
    }
    if (cur) {
      last = cur.last;
      if (last.MSG && last.MSG !== lastMsg) con.print(last.MSG.trimEnd());   // the message line goes to the console too, as Con_Printf's
      lastMsg = last.MSG;
      ticAt = performance.now(); ticCount += cur.tics;
      perf.tic = cur.ticMs; perf.faces = cur.facesMs;
      if (cur.sounds.length) lastSoundId = cur.sounds[cur.sounds.length - 1][0];
      if (cur.fx.length) lastFxId = cur.fx[cur.fx.length - 1][0];
      if (cur.demoDone) { demoPlayer = null; updateDemoButtons(); flash('the demo has ended: the game is yours'); }
      // EF_MUZZLEFLASH for the player: a shot starts the weapon's animation (the axe has no flash; the
      // nailguns and the lightning gun cycle their frames, one shot a frame)
      if (last.WEAPONFRAME !== prevWeaponFrame && last.WEAPONFRAME > 0 && last.WEAPON !== 4096 && (last.WEAPONFRAME === 1 || (last.WEAPON & (4 | 8 | 64)))) muzzleUntil = last.TIME_ + 0.1;
      prevWeaponFrame = last.WEAPONFRAME;

      // the start map's halls (trigger_setskill) choose the skill, as Quake's do: the next level is spawned with it
      if (last.SKILL != null && last.SKILL !== settings.skill) { settings.skill = last.SKILL; $('skill').value = String(last.SKILL); saveSettings(); }
      // what progs.dat told the client: the finale's text starts typing; svc_cdtrack changes the music
      if (last.INTERMISSION === 2 && !finaleStart) finaleStart = last.TIME_;
      if (last.CDTRACK >= 0 && last.CDTRACK !== cdTrack) { cdTrack = last.CDTRACK; audio.playMusic(cdTrack); }

      if (last.EXIT_KIND === 1 && last.NEXT_MAP) {
        // QuakeC mode has shown its intermission and waited for fire already; the PSQL game shows it now
        if (settings.logic === 'qc') { await nextLevel(); nextFrame(); return; }
        interWait = performance.now();
      }
      if (last.FINALE === 1 && !finaleShown) {
        // Shub-Niggurath is dead: the ending, then back to the start map
        finaleShown = true;
        setStatus('Congratulations and well done! You have beaten the hideous Shub-Niggurath, and its hordes of spawn. The Quake realm is free.');
        await new Promise((r) => setTimeout(r, 12000));
        finaleShown = false;
        await startMap('start', true);
        nextFrame();
        return;
      }
      if (last.EXIT_KIND === 3) {
        await startMap(map.name, true);
        nextFrame();
        return;
      }
      const { faces, ents, styles, sounds, fx, bframes, tics } = cur;
      brushFrames.clear();
      for (const [id, f] of bframes) brushFrames.set(id, f);
      perf.rows = faces.length;
      const styleMap = new Float32Array(64);
      for (const [s, v] of styles) if (s < 64) styleMap[s] = v;
      const listener = { x: last.PX, y: last.PY, z: last.VIEW_Z, yaw: last.YAW };
      if (sounds.length) audio.playEvents(sounds, listener);
      audio.update(listener, [last.AMB_WATER, last.AMB_SKY], tics * 0.05);
      if (fx.length) handleFx(fx, last.TIME_);
      lastDraw = { faces, ents, styles: styleMap };
    }
    // the next tic and its queries, in the engine's worker, while this frame is painted
    if (!interWait && !pending) pending = issue();
    if (!lastDraw) { nextFrame(); return; }   // the level's first tic is on its way

    const now = performance.now();
    const t = now;
    drawFrame(lastDraw.faces, lastDraw.ents, lastDraw.styles, last.TIME_ + (now - ticAt) / 1000, Math.min(0.1, (now - drawAt) / 1000));
    drawAt = now;
    perf.draw = performance.now() - t;
    updateStats();
  } catch (err) {
    console.error(err);
    setStatus(`Error: ${err.message}`, true);
    running = false;
    return;
  }
  nextFrame();
}

// the level is over (changelevel): the next map, carrying the player, or the start map past the pak's last
async function nextLevel() {
  const next = last.NEXT_MAP.toLowerCase();
  setStatus(`${map.name} completed — kills ${last.KILLED}/${last.TOTAL_MONSTERS}, secrets ${last.FOUND_SECRETS}/${last.TOTAL_SECRETS}`);
  if (pak.has(`maps/${next}.bsp`)) await startMap(next, false);
  else { setStatus(`${next} is not in this pak (shareware ends here)`); await new Promise((r) => setTimeout(r, 2500)); await startMap('start', false); }
}

function handleFx(rows, time) {
  for (const [, kind, x, y, z, x2, y2, z2, n] of rows) {
    switch (kind) {
      case 1: renderer.spawnParticles('gunshot', x, y, z, 20, [0, 0, 0], 0); break;
      case 2: renderer.spawnParticles('explosion', x, y, z, 0); explosions.push({ x, y, z, t0: time }); break;
      case 3: renderer.spawnParticles('blood', x, y, z, Math.min(n * 2, 60), [0, 0, 0], 73); break;
      case 4: beams.push({ a: [x, y, z], b: [x2, y2, z2], until: time + 0.2, owner: n }); break;
      case 5: renderer.spawnParticles('teleport', x, y, z, 0); break;
      case 6: renderer.spawnParticles('gunshot', x, y, z, n ? 20 : 10, [0, 0, 0], 0); break;
      case 7: renderer.spawnParticles('lava', x, y, z, 0); explosions.push({ x, y, z, t0: time }); break;
      default: break;
    }
  }
}

// V_CalcRoll: strafing rolls the view up to cl_rollangle (2°), fully from cl_rollspeed (200 units a second);
// the sideways speed is the player's motion between frames along the view's right
let rollFrom = null;
function viewRoll(time) {
  let roll = 0;
  if (rollFrom && time > rollFrom.t) {
    const vx = (last.PX - rollFrom.x) / (time - rollFrom.t), vy = (last.PY - rollFrom.y) / (time - rollFrom.t);
    const yaw = (last.YAW * Math.PI) / 180;
    const side = vx * Math.sin(yaw) - vy * Math.cos(yaw);
    roll = Math.sign(side) * (Math.abs(side) < 200 ? (Math.abs(side) * 2) / 200 : 2);
  } else if (rollFrom) roll = rollFrom.roll;
  if (!rollFrom || time > rollFrom.t) rollFrom = { x: last.PX, y: last.PY, t: time, roll };
  return roll;
}

// V_ParseDamage's view kick: a row that took a hit carries its pitch and roll (worked out in SQL from where the hit
// came from, DMG_PITCH and DMG_ROLL); V_CalcViewRoll adds them to the view, fading out over v_kicktime (0.5 s)
let kick = null;
function viewKick(time) {
  if ((last.DMG_PITCH || last.DMG_ROLL) && kick?.at !== last.TIME_) kick = { pitch: last.DMG_PITCH, roll: last.DMG_ROLL, at: last.TIME_ };
  const f = kick && !last.DEAD && !last.INTERMISSION ? Math.max(0, 1 - (time - kick.at) / 0.5) : 0;
  return f > 0 ? { pitch: kick.pitch * f, roll: kick.roll * f } : { pitch: 0, roll: 0 };
}

function drawFrame(faces, ents, styles, time, dt = 0.05, overlay = null) {
  const r = renderer;
  const k = viewKick(time);
  const view = { x: last.PX, y: last.PY, z: last.VIEW_Z, yaw: last.YAW, pitch: last.PITCH + k.pitch, roll: (last.DEAD ? 60 : last.INTERMISSION ? 0 : viewRoll(time)) + k.roll, fov: settings.fov };
  r.beginFrame(view);
  const dl = r.dlights = frameDlights({ ents, models: res.models, explosions, qc: settings.logic === 'qc', time,
    player: { x: last.PX, y: last.PY, z: last.PZ, yaw: last.YAW, muzzle: muzzleUntil > time - 0.05 && !last.DEAD, glow: !!(last.QUAD || last.INVINCIBLE) } });
  const entFrames = new Map();
  for (const e of ents) if (e[12] === 'B') entFrames.set(e[0], e[2]);
  if (faces) {
    // brush-model frames (button textures) come from the ents table via frame_ents? Brush ents are
    // not in FRAME_ENTS; their frame is in the faces' ent rows only via a side query — keep a cache.
    if (settings.renderer === 'sql') r.drawFaces(faces, styles, time, brushFrames);   // vertex rows from FRAME_FACES
    else r.drawFaceList(faces, styles, time, brushFrames);                             // face rows from FRAME_FACES_FAST
  }
  // alias models and sprites
  const bsp = map.bsp;
  for (const e of ents) {
    const [, mid, frame, skin, x, y, z, pitch, yaw, roll, effects, alpha, kindRaw, , colors] = e;
    const kind = String(kindRaw).trim();   // CHAR(1) comes back padded
    const m = res.models.get(mid);
    if (!m) continue;
    if (kind === 'M') {
      const light = effects & 8 ? 255 : Math.min(255, Math.max(lightPoint(bsp, x, y, z + 8), effects & 4 ? 160 : 0) + dlightAt(dl, x, y, z));
      const spin = m.mdl.flags & 8 ? (time * 100) % 360 : 0;   // EF_ROTATE items spin
      r.drawAlias(m.mdl, frame, skin, [x, y, z], [pitch, yaw + spin, roll], light, { time, alpha: alpha === 1, colors });
    } else if (kind === 'S') {
      r.drawSprite(m.spr, frame, [x, y, z]);
    }
  }
  // the see-through liquids (r_wateralpha), over the world and the models
  r.drawDeferred(time);
  // lightning beams: bolt segments every 30 units; the player's own beam starts from where the player is now
  // (CL_UpdateTEnts moves the view entity's beam to its origin each frame)
  const bolt = res.models.get(res.byName.get('progs/bolt2.mdl'));
  beams = beams.filter((b) => b.until > time);
  for (const b of beams) {
    if (b.owner === playerEnt) b.a = [last.PX, last.PY, last.PZ];
    const d = [b.b[0] - b.a[0], b.b[1] - b.a[1], b.b[2] - b.a[2]];
    const len = Math.hypot(...d) || 1;
    const yaw = (Math.atan2(d[1], d[0]) * 180) / Math.PI;
    const pitch = (Math.atan2(d[2], Math.hypot(d[0], d[1])) * 180) / Math.PI;
    for (let k = 0; k < len; k += 30) {
      const p = [b.a[0] + (d[0] * k) / len, b.a[1] + (d[1] * k) / len, b.a[2] + (d[2] * k) / len];
      if (bolt) r.drawAlias(bolt.mdl, 0, 0, p, [pitch, yaw, Math.random() * 360], 255, { time });
    }
  }
  // explosion sprites: 6 frames at 10 Hz
  const explod = res.models.get(res.byName.get('progs/s_explod.spr'));
  explosions = explosions.filter((x) => time - x.t0 < 0.6);
  for (const x of explosions) if (explod) r.drawSprite(explod.spr, Math.floor((time - x.t0) * 10), [x.x, x.y, x.z]);
  r.runParticles(dt, time);
  r.drawParticles();
  // the weapon in hand (depth hack: drawn over everything near)
  if (!last.DEAD && !last.INTERMISSION && last.WEAPON && VIEW_MODELS[last.WEAPON]) {
    const vm = res.models.get(res.byName.get(VIEW_MODELS[last.WEAPON]));
    if (vm) {
      const bob = Math.sin(time * 8) * Math.min(1, Math.hypot(last.PX - (prevPos?.x ?? last.PX), last.PY - (prevPos?.y ?? last.PY)) / 8) * 1.5;
      const light = Math.min(255, Math.max(lightPoint(bsp, last.PX, last.PY, last.PZ), 32) + dlightAt(dl, last.PX, last.PY, last.PZ));
      r.zb.fill(0, 0, r.w * r.h);   // the gun is always in front
      r.drawAlias(vm.mdl, Math.min(last.WEAPONFRAME, vm.mdl.frames.length - 1), 0, [last.PX, last.PY, last.VIEW_Z + 2 + bob], [-last.PITCH, last.YAW, 0], light, { near: 1, time });
    }
  }
  prevPos = { x: last.PX, y: last.PY };
  // the eye under water, slime or lava: the warp, over everything 3D and under the 2D
  if (last.WATERLEVEL >= 3) r.warpView(time);

  // 2D
  // the intermission (the stats, or the finale's text) replaces the status bar, as in Sbar_Draw
  if (last.INTERMISSION === 2) hud.drawFinale(r, last.FINALE_TEXT ?? '', time - finaleStart);
  else if (last.INTERMISSION === 1) hud.drawIntermission(r, last);
  else hud.draw(r, last, time);
  if (last.CPRINT && !last.INTERMISSION) hud.drawCenter(r, last.CPRINT, Math.floor(r.h * 0.35));
  // the notify lines: the message line and what the console printed in the last three seconds (Con_DrawNotify)
  con.drawNotify(r, performance.now() / 1000);
  if (last.DEAD) hud.drawCenter(r, settings.mode === 'single' ? 'you died\n\npress fire to restart' : 'press fire to respawn', 60);
  // deathmatch: the frags, best first, the player's line marked (Sbar_DrawFrags, as a list)
  if (settings.mode === 'deathmatch' && scores.length && !last.INTERMISSION) {
    [...scores].sort((a, b) => b[2] - a[2]).forEach(([c, name, frags], i) => {
      const line = `${String(frags).padStart(3)} ${String(name ?? '').slice(0, 10)}`;
      r.drawString(hud.conchars, (c === 1 ? '>' : ' ') + line, r.w - 8 * 15, 10 + i * 8, c !== 1);
    });
  }
  // the palette blend: damage, bonus, water
  let tint = null;
  const since = time - last.DMG_TIME;
  if (since >= 0 && since < 0.5 && (last.DMG_TAKE || last.DMG_SAVE)) {
    const a = Math.min(0.6, (last.DMG_TAKE * 0.03 + last.DMG_SAVE * 0.01) * (1 - since / 0.5) + 0.1);
    tint = [255, 0, 0, a];
  } else if (time - last.BONUS_TIME >= 0 && time - last.BONUS_TIME < 0.4) tint = [215, 186, 69, 0.3 * (1 - (time - last.BONUS_TIME) / 0.4)];
  else if (last.QUAD) tint = [0, 0, 255, 0.2];
  else if (last.INVINCIBLE) tint = [255, 255, 0, 0.3];
  else if (last.SUIT) tint = [0, 255, 0, 0.2];
  else if (last.WATERLEVEL >= 3) tint = last.WATERTYPE === -5 ? [255, 80, 0, 0.6] : last.WATERTYPE === -4 ? [0, 25, 5, 0.6] : [130, 80, 50, 0.5];
  if (overlay) overlay(r);
  con.draw(r, performance.now() / 1000);
  r.present(tint);
}
let prevPos = null;
const brushFrames = new Map();

let fpsT = performance.now(), fpsN = 0, fps = 0, tps = 0;
function updateStats() {
  fpsN++;
  const now = performance.now();
  if (now - fpsT > 500) { fps = (fpsN * 1000) / (now - fpsT); tps = (ticCount * 1000) / (now - fpsT); ticCount = 0; fpsT = now; fpsN = 0; }
  statsEl.textContent = `${fps.toFixed(1)} fps, ${tps.toFixed(0)} tics/s · ${settings.logic === 'qc' ? `qc_tic (${jit?.compiled.size ?? 0} functions compiled)` : 'quake_tic'} ${perf.tic.toFixed(0)} ms · frame queries ${perf.faces.toFixed(0)} ms (${perf.rows} vertex rows) · raster ${perf.draw.toFixed(0)} ms · ${renderer.particles.length} particles`;
}

// ── SQL console ─────────────────────────────────────────────────────────
async function runConsole() {
  const sqlText = $('sql').value.trim();
  if (!sqlText || !db) return;
  const out = $('sql-out');
  remember(sqlText);
  const t0 = performance.now();
  try {
    const r = /^\s*(select|with|execute\s+block)/i.test(sqlText)
      ? await db.query(sqlText, [], { rowMode: 'object' })
      : { rows: [], exec: await db.exec(sqlText) };
    const ms = (performance.now() - t0).toFixed(1);
    if (!r.rows.length) { out.textContent = `OK (${ms} ms)`; return; }
    const cols = Object.keys(r.rows[0]);
    const lines = [cols.join('\t'), ...r.rows.slice(0, 200).map((row) => cols.map((c) => fmt(row[c])).join('\t'))];
    out.textContent = `${r.rows.length} row(s), ${ms} ms\n${lines.join('\n')}`;
  } catch (err) {
    out.textContent = err.message;
  }
}
const fmt = (v) => (typeof v === 'number' && !Number.isInteger(v) ? v.toFixed(2) : String(v));
$('run-sql').addEventListener('click', runConsole);

// History: every statement run, kept in localStorage; ↑ and ↓ walk it from the first or last line of the
// text (Alt+↑/↓ from anywhere), the statement being typed kept at the end. Completion: Tab completes the
// word before the caret from the database's own names (tables, columns, procedures, functions, read once
// from the RDB$ tables) and the usual keywords; Tab again cycles the matches, listed in the output.
const HISTORY_KEY = 'firebird-quake:sql-history';
let history = [];
try { history = JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]'); } catch { /* none */ }
let histPos = history.length, draft = '';
function remember(text) {
  history = [...history.filter((h) => h !== text), text].slice(-100);
  histPos = history.length;
  try { localStorage.setItem(HISTORY_KEY, JSON.stringify(history)); } catch { /* ignore */ }
}
let names = null, namesLoading = null;
function catalogue() {   // started when the console is first focused; a Tab before it is done completes nothing
  if (names || namesLoading || !db) return;
  const q = async (s) => (await db.query(s, [], { rowMode: 'array' })).rows.map((r) => String(r[0]).trim().toLowerCase());
  namesLoading = (async () => { names = [...new Set([
    ...await q('SELECT rdb$relation_name FROM rdb$relations WHERE COALESCE(rdb$system_flag, 0) = 0'),
    ...await q('SELECT DISTINCT rdb$field_name FROM rdb$relation_fields WHERE COALESCE(rdb$system_flag, 0) = 0'),
    ...await q('SELECT rdb$procedure_name FROM rdb$procedures WHERE COALESCE(rdb$system_flag, 0) = 0'),
    ...await q('SELECT rdb$function_name FROM rdb$functions WHERE COALESCE(rdb$system_flag, 0) = 0'),
    ...('select from where and or not in is null order by group having join on as update set delete insert into values '
      + 'execute procedure block returns begin end declare variable first rows count sum min max avg distinct cast integer '
      + 'double precision varchar coalesce iif case when then else exists between like starting with').split(' '),
  ])].sort(); })();
}
$('sql').addEventListener('focus', catalogue);
let completion = null;   // the Tab in progress: the text it made, where the word starts and ends, the matches, which is shown
$('sql').addEventListener('keydown', (e) => {
  const ta = e.target;
  if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { runConsole(); return; }
  if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
    const up = e.key === 'ArrowUp';
    if (!e.altKey && (up ? ta.value.slice(0, ta.selectionStart).includes('\n') : ta.value.slice(ta.selectionEnd).includes('\n'))) return;
    if (up ? histPos === 0 : histPos >= history.length) return;
    e.preventDefault();
    if (histPos === history.length) draft = ta.value;
    histPos += up ? -1 : 1;
    ta.value = histPos === history.length ? draft : history[histPos];
    ta.setSelectionRange(ta.value.length, ta.value.length);
    return;
  }
  if (e.key === 'Tab' && !e.shiftKey) {
    e.preventDefault();
    if (!names) { catalogue(); return; }
    const end = ta.selectionStart;
    if (!completion || completion.end !== end || completion.value !== ta.value) {
      const start = ta.value.slice(0, end).search(/[\w$]*$/);
      const prefix = ta.value.slice(start, end);
      if (!prefix) return;
      const upper = prefix === prefix.toUpperCase() && /[A-Z]/.test(prefix);
      completion = { start, end, i: -1, matches: names.filter((n) => n.startsWith(prefix.toLowerCase()) && n !== prefix.toLowerCase()).map((n) => (upper ? n.toUpperCase() : n)) };
    }
    if (!completion.matches.length) return;
    completion.i = (completion.i + 1) % completion.matches.length;
    const word = completion.matches[completion.i];
    ta.setRangeText(word, completion.start, completion.end, 'end');
    completion.end = completion.start + word.length;
    completion.value = ta.value;
    if (completion.matches.length > 1) $('sql-out').textContent = completion.matches.slice(0, 60).join('  ') + (completion.matches.length > 60 ? ' …' : '');
    return;
  }
  completion = null;
});
for (const b of document.querySelectorAll('[data-sql]')) b.addEventListener('click', () => { $('sql').value = b.dataset.sql; runConsole(); });

// ── boot ─────────────────────────────────────────────────────────────
const SCHEMA_IMAGE = __SCHEMA_IMAGE__;   // the build's schema image (scripts/build.mjs), or null
let schemaFromImage = false;
async function schemaImage() {
  if (!SCHEMA_IMAGE) return null;
  try {
    const r = await fetch(new URL(`./${SCHEMA_IMAGE}`, location.href));
    if (!r.ok) return null;
    let bytes = new Uint8Array(await r.arrayBuffer());
    // gzip's magic number: a server that sent it with Content-Encoding has had the browser unpack it already
    if (bytes[0] === 0x1f && bytes[1] === 0x8b) bytes = new Uint8Array(await new Response(new Blob([bytes]).stream().pipeThrough(new DecompressionStream('gzip'))).arrayBuffer());
    schemaFromImage = true;
    return bytes;
  } catch { return null; }
}

// The paks, kept in the Cache API after the first visit: 18 MB of shareware pak not fetched again. The
// key carries the server's validator (ETag, else Last-Modified and length, from the HEAD the data sets
// already make), so a changed pak is fetched anew and the old copy dropped.
const PAK_CACHE = 'firebird-quake-paks';
async function cachedBuffer(rel, validator) {
  const url = new URL('./' + rel, location.href).href;
  const key = `${url}?v=${encodeURIComponent(validator || '')}`;
  let cache = null;
  try { cache = await caches.open(PAK_CACHE); } catch { /* no Cache API (an insecure origin): fetch */ }
  if (cache && validator) {
    const hit = await cache.match(key);
    if (hit) return hit.arrayBuffer();
  }
  const r = await fetch(url);
  if (!r.ok) throw new Error(`could not fetch ${rel} (${r.status})`);
  const buf = await r.arrayBuffer();
  if (cache && validator) {
    try {
      for (const old of await cache.keys()) if (old.url.startsWith(`${url}?v=`) && old.url !== key) await cache.delete(old);
      await cache.put(key, new Response(buf, { headers: { 'content-type': 'application/octet-stream' } }));
    } catch { /* storage full or refused: next time it is fetched again */ }
  }
  return buf;
}
const FB_THREADS = __FB_THREADS__;      // the engine build (scripts/build.mjs): threaded only with FB_THREADS=1
async function openDatabase() {
  // the build without threads needs no SharedArrayBuffer, so no cross-origin isolation; the threaded one does
  if (FB_THREADS && !window.crossOriginIsolated && window.isSecureContext && 'serviceWorker' in navigator &&
      Number(sessionStorage.getItem('firebird-quake:coi-reloads') || '0') < 2) {
    setStatus('Enabling cross-origin isolation for Firebird WASM (one-time reload)…');
    return new Promise(() => {});
  }
  if (FB_THREADS && !window.crossOriginIsolated) {
    throw new Error('This page is not cross-origin isolated, so Firebird WASM cannot start. Reload once (the service worker enables it), and use HTTPS or localhost.');
  }
  setStatus('Starting Firebird 6 (WebAssembly)…');
  const t0 = performance.now();
  // the schema as the build made it (scripts/build.mjs: every table and procedure, dumped and gzipped), so
  // the database opens ready instead of compiling the PSQL; without it, or if it will not open, as before
  const image = await schemaImage();
  const open = (bytes) => new FirebirdBrowser('memory://quake', {
    worker: new Worker(new URL('./firebird-engine-worker.js', import.meta.url)),
    multiTab: 'allow-unsafe',
    autoPersist: false,
    ...(bytes ? { loadDataDir: bytes } : {}),
  });
  let instance = open(image);
  let v;
  try {
    v = await instance.query("SELECT rdb$get_context('SYSTEM', 'ENGINE_VERSION') AS v FROM rdb$database");
    if (image) await instance.query('SELECT COUNT(*) FROM rdb$procedures WHERE rdb$procedure_name = \'QUAKE_TIC\'');
  } catch (err) {
    if (!image) throw err;
    console.warn('[firebird-quake] the schema image did not open; building the schema', err);
    instance = open(null);
    v = await instance.query("SELECT rdb$get_context('SYSTEM', 'ENGINE_VERSION') AS v FROM rdb$database");
    schemaFromImage = false;
  }
  $('engine').textContent = `Firebird ${v.rows[0].V}`;
  if (image && schemaFromImage) { console.log(`[firebird-quake] database ready in ${(performance.now() - t0).toFixed(0)} ms (the schema from ${SCHEMA_IMAGE})`); return instance; }
  setStatus('Creating the Quake schema (PSQL)…');
  await createSchema(instance, { schema: schemaSql, physics: physicsSql, game: gameSql, movers: moversSql, triggers: triggersSql, items: itemsSql, combat: combatSql, spawn: spawnSql, weapons: weaponsSql, monsters: monstersSql, render: renderSql, qcvm: qcvmSql, qcbuiltins: qcbuiltinsSql, qcexec: qcexecSql, qcserver: qcserverSql, qcphysics: qcphysicsSql, qcpage: qcpageSql, bots: botsSql, host: hostSql, save: saveSql, demo: demoSql });
  console.log(`[firebird-quake] database ready in ${(performance.now() - t0).toFixed(0)} ms (the schema built)`);
  return instance;
}

function usePak(buffers, label) { return serial(() => loadData(buffers, label)); }
async function loadData(buffers, label) {
  running = false;
  await drain();
  progsLoaded = false;
  base = { buffers, label };
  // pak0.pak, and pak1.pak if you own Quake; a mod's files over them, as a game directory over id1
  pak = new PakSet([...buffers.map((b) => new Pak(b)), ...(mod?.paks ?? [])]);
  if (mod) label = `${label} + ${mod.name}`;
  const maps = pak.mapNames();
  if (!maps.length) throw new Error(`${label} has no maps`);
  setStatus(`Copying ${label} models into Firebird…`);
  res = await loadResources(db, pak, { width: viewWidth(), height: viewHeight() - sbarLines(), fov: settings.fov });
  const palette = loadPalette(pak.get('gfx/palette.lmp'));
  const colormap = pak.get('gfx/colormap.lmp');
  wad = new Wad2(pak.get('gfx.wad'));
  renderer = new Renderer(canvas, { palette, colormap });
  applyLiquids();
  renderer.sbarLines = sbarLines();
  renderer.setSize(viewWidth(), viewHeight());
  hud = new Hud(wad, (n) => (pak.has(n) ? qpic(pak.get(n)) : null));
  con.conchars = hud.conchars;
  con.conback = pak.has('gfx/conback.lmp') ? qpic(pak.get('gfx/conback.lmp')) : null;
  menu = new Menu({ lmp: (n) => (pak.has(n) ? qpic(pak.get(n)) : null), conchars: hud.conchars, play: (snd) => audio.playLocal(snd), actions: menuActions() });
  audio.setPak(pak);
  $('map').innerHTML = maps.map((m) => `<option>${m}</option>`).join('');
  $('pakname').textContent = label;
  pakKey = label;
  await refreshSaves();
  try { demo = SaveStore.available() ? (await SaveStore.get(pakKey, 'demo')) ?? null : null; } catch { demo = null; }
  updateDemoButtons();
  const first = maps.includes(settings.map) ? settings.map : maps.includes('start') ? 'start' : maps[0];
  await loadLevel(first, true);   // (inside this load's turn: startMap would wait for it)
}

async function boot() {
  try {
    db = await openDatabase();
    // for the devtools console: await quake.sql('SELECT * FROM player'); quake.renderer, quake.res, quake.settings, quake.last;
    // the QuakeC VM: await quake.loadProgs(); await quake.sql("EXECUTE PROCEDURE qc_run('worldspawn', 0)"); await quake.sql('SELECT * FROM qc_log')
    window.quake = { db, audio, settings, sql: (q, p) => db.query(q, p).then((r) => r.rows), loadProgs: () => loadProgs(db, pak), get jit() { return jit; }, get menu() { return menu; }, con, get binds() { return binds; }, get renderer() { return renderer; }, get res() { return res; }, get last() { return last; }, get map() { return map; } };
    // which game data the site serves: the shareware pak, the registered pak1.pak beside it, LibreQuake
    // (public/pak/lq1/, `npm run fetch-pak -- --librequake`), or the shareware pak with LibreQuake's
    // pak1.pak, which gives the registered monsters free models
    const DATASETS = {
      shareware: { label: 'Quake shareware', paks: ['pak/pak0.pak'] },
      registered: { label: 'Quake (registered pak1.pak)', paks: ['pak/pak0.pak', 'pak/pak1.pak'] },
      'shareware+lq1': { label: 'Quake shareware + LibreQuake monsters', paks: ['pak/pak0.pak', 'pak/lq1/pak1.pak'] },
      librequake: { label: 'LibreQuake', paks: ['pak/lq1/pak0.pak', 'pak/lq1/pak1.pak'] },
    };
    // a pak the site serves: its validator (for the cache), or false
    const served = async (rel) => {
      try {
        const r = await fetch(new URL('./' + rel, location.href), { method: 'HEAD', cache: 'no-cache' });
        if (!r.ok || (r.headers.get('content-type') ?? '').indexOf('text/html') >= 0) return false;
        return r.headers.get('etag') || `${r.headers.get('last-modified') ?? ''}/${r.headers.get('content-length') ?? ''}`;
      } catch { return false; }
    };
    const have = Object.fromEntries(await Promise.all([...new Set(Object.values(DATASETS).flatMap((d) => d.paks))].map(async (p) => [p, await served(p)])));
    const sets = Object.entries(DATASETS).filter(([, d]) => d.paks.every((p) => have[p]));
    $('data').innerHTML = sets.map(([k, d]) => `<option value="${k}">${d.label}</option>`).join('');
    $('data').parentElement.hidden = sets.length <= 1;
    const loadDataset = async (key) => {
      const d = DATASETS[key];
      setStatus(`Downloading ${d.paks.map((p) => p.slice(4)).join(' + ')}…`);
      const buffers = await Promise.all(d.paks.map((p) => cachedBuffer(p, have[p])));
      await usePak(buffers, d.paks.map((p) => p.slice(4)).join(' + '));
    };
    $('data').addEventListener('change', (e) => { settings.data = e.target.value; saveSettings(); loadDataset(e.target.value).catch((err) => setStatus(err.message, true)); });   // the frame loop keeps running; usePak pauses it while loading
    // the mods the site serves (public/pak/mods/, npm run fetch-mods)
    try {
      const r = await fetch(new URL('./pak/mods/index.json', location.href));
      const list = r.ok && (r.headers.get('content-type') ?? '').indexOf('text/html') < 0 ? await r.json() : [];
      $('mod').innerHTML = '<option value="">none</option>' + list.map((m) => `<option value="${m.file}" title="${m.note}">${m.name}</option>`).join('');
    } catch { /* no mods served: the file picker still takes one */ }
    if (!sets.length) throw new Error('could not fetch pak0.pak; pick a PAK file instead');
    const key = sets.some(([k]) => k === settings.data) ? settings.data : sets[0][0];
    $('data').value = key;
    await loadDataset(key);
    nextFrame();
  } catch (err) {
    console.error(err);
    setStatus(err.message, true);
  }
}

$('pakfile').addEventListener('change', async (e) => {
  const files = [...e.target.files].sort((a, b) => a.name.localeCompare(b.name));   // pak0.pak before pak1.pak
  if (!files.length || !db) return;
  try { await usePak(await Promise.all(files.map((f) => f.arrayBuffer())), files.map((f) => f.name).join(' + ')); } catch (err) { setStatus(err.message, true); }
});
// a mod: a zip as released (or pak files) laid over the data set's paks; with its own progs.dat its game
// runs in the QuakeC VM, so the logic switches to QuakeC
async function useMod(next) {
  mod = next;
  if (mod?.progs && settings.logic !== 'qc') {   // (not setLogic: the level is loaded below, once)
    settings.logic = 'qc'; $('logic').value = 'qc'; saveSettings();
    flash(`${mod.name} brings its own progs.dat: the game runs in QuakeC mode`);
  }
  $('impulse-box').hidden = !mod?.progs;
  if (base) await usePak(base.buffers, base.label);
}
// a mod's impulses (FrikBot X's 100 adds a bot): the next tic carries it, as a key bound to it would
$('impulse-send').addEventListener('click', () => { impulse = Math.max(1, Math.min(255, Number($('impulse-n').value) || 0)); canvas.focus(); });
async function modFromFiles(files) {
  const zips = files.filter((f) => /\.zip$/i.test(f.name));
  if (zips.length) return modFromZip(await zips[0].arrayBuffer(), zips[0].name);
  const paks = await Promise.all(files.sort((a, b) => a.name.localeCompare(b.name)).map(async (f) => new Pak(await f.arrayBuffer())));
  return { name: files.map((f) => f.name).join(' + '), paks, progs: paks.some((p) => p.has('progs.dat')) };
}
$('modfile').addEventListener('change', async (e) => {
  const files = [...e.target.files];
  if (!files.length || !db) return;
  try { $('mod').value = ''; await useMod(await modFromFiles(files)); } catch (err) { setStatus(err.message, true); }
});
$('mod').addEventListener('change', async (e) => {
  const file = e.target.value;
  try {
    if (!file) { await useMod(null); return; }
    setStatus(`Downloading ${file}…`);
    const r = await fetch(new URL(`./pak/mods/${file}`, location.href));
    if (!r.ok) throw new Error(`could not fetch ${file} (${r.status})`);
    await useMod(await modFromZip(await r.arrayBuffer(), file));
  } catch (err) { setStatus(err.message, true); }
});
$('map').addEventListener('change', (e) => { settings.map = e.target.value; saveSettings(); startMap(e.target.value, true).catch((err) => setStatus(err.message, true)); });
// the settings, changed from the page's controls or from the menu's options
async function setDetail(v) {
  settings.detail = v; saveSettings(); $('detail').value = v;
  await setView(db, viewWidth(), viewHeight() - sbarLines(), settings.fov);
  renderer.sbarLines = sbarLines();
  renderer.setSize(viewWidth(), viewHeight());
}
function setLogic(v) {
  settings.logic = v; saveSettings(); $('logic').value = v;
  if (v !== 'qc' && settings.mode !== 'single') { settings.mode = 'single'; $('mode').value = 'single'; saveSettings(); }   // the bots are QuakeC clients
  if (map) startMap(map.name, true).catch((err) => setStatus(err.message, true));   // a new game under the other logic
}
// single player, or a deathmatch or coop game with bots (QuakeC mode: progs.dat's rules, sql/bots.sql)
function setMode(v, restart = true) {
  settings.mode = v; $('mode').value = v;
  if (v !== 'single' && settings.logic !== 'qc') { settings.logic = 'qc'; $('logic').value = 'qc'; }
  saveSettings();
  if (restart && map) startMap(map.name, true).catch((err) => setStatus(err.message, true));
}
// r_wateralpha: liquids drawn see-through at half where the map was vised for it (LibreQuake's are; id's are not)
// (opaque: every liquid opaque, as Quake; translucent: the map's alphas, else r_wateralpha and the rest)
function applyLiquids() {
  if (!renderer) return;
  settings.alphas = { water: 0.5, lava: 0, slime: 0, tele: 0, ...settings.alphas };
  renderer.liquidAlpha = settings.liquids === 'translucent' ? Math.min(0.999, settings.alphas.water) : 1;
  renderer.liquidAlphas = { lava: settings.alphas.lava, slime: settings.alphas.slime, tele: settings.alphas.tele };
}
function setLiquids(v) { settings.liquids = v; saveSettings(); $('liquids').value = v; applyLiquids(); }
function setRenderer(v) { settings.renderer = v; saveSettings(); $('renderer').value = v; }
function setSfx(v) { settings.sfx = v; saveSettings(); $('sfxvol').value = v; audio.unlock(); audio.setVolume(v / 100); }
function setMusicVolume(v) { settings.music = v; saveSettings(); $('musicvol').value = v; audio.setMusicVolume(v / 100); }
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

// M_Menu_Options and the rest of the menu's actions: New Game is Quake's "map start"
// r_wateralpha and its kin: alone, the value; with one, set and kept (the map's own keys still come first)
// (a declaration, hoisted: consoleCommands() runs at start-up, above this line)
function alphaCmd(k) {
  return ([v]) => {
    if (v === undefined) return `"r_${k}alpha" is "${settings.alphas?.[k] ?? (k === 'water' ? 0.5 : 0)}"`;
    settings.alphas = { water: 0.5, lava: 0, slime: 0, tele: 0, ...settings.alphas, [k]: Math.max(0, Math.min(1, Number(v) || 0)) };
    saveSettings(); applyLiquids();
    return settings.liquids === 'translucent' ? null : 'Liquids is set to opaque: set it to translucent to see through them';
  };
}

// the console's commands (cmd.c, host_cmd.c, keys.c): the game's become host_cmd's SQL (sql/host.sql)
function consoleCommands() {
  const need = () => { if (!running || !db) throw new Error('no game running'); };
  const host = async (cmd, a1 = '', a2 = '') => { need(); return (await db.query('SELECT msg FROM host_cmd(?, ?, ?)', [cmd, a1, a2])).rows[0]?.MSG ?? null; };
  const slotOf = (a) => (a === undefined || a === 'quick' ? QUICK_SLOT : Number(String(a).replace(/^s/i, '')));
  const value = (key, apply) => (args) => {
    if (!args.length) return `"${key}" is "${settings[key]}"`;
    const v = Number(args[0]);
    if (!Number.isFinite(v)) return `${key}: a number`;
    return apply(v);
  };
  const C = {
    help: { help: 'these commands', run: () => [...Object.entries(C).map(([n, c]) => `${n.padEnd(13)} ${c.help}`),
      `${'alias'.padEnd(13)} [name] [commands]: a name for a line of commands`, `${'exec'.padEnd(13)} <file>: a config file from the paks (default.cfg…)`].join('\n') },
    clear: { help: 'empty the console', run: (a, c) => { c.lines = []; } },
    echo: { help: 'print the text', run: (a, c, raw) => raw },
    map: { help: '<map>: a new game on that map', run: async ([m]) => { if (!m) return `map is ${map?.name}`; if (!pak.has(`maps/${m}.bsp`)) return `map ${m} not found`; await startMap(m, true); } },
    changelevel: { help: '<map>: go on to that map, keeping what you carry', run: async ([m]) => { need(); if (!pak?.has(`maps/${m}.bsp`)) return `map ${m} not found`; await startMap(m, false); } },
    restart: { help: 'this map again, from the start', run: async () => { need(); await startMap(map.name, true); } },
    skill: { help: '<0..3>: the skill of the next map', run: value('skill', (v) => { settings.skill = Math.max(0, Math.min(3, Math.round(v))); $('skill').value = String(settings.skill); saveSettings(); }) },
    god: { help: 'take no damage', run: () => host('god') },
    notarget: { help: 'monsters do not see you', run: () => host('notarget') },
    color: { help: '<top> [bottom]: your shirt and pants, 0..13 (what the bots see in deathmatch)', run: (a) => host('color', a[0] ?? '', a[1] ?? '') },
    noclip: { help: 'fly through walls', run: () => host('noclip') },
    give: { help: '<2..8 | s n r c h> <amount>: a weapon, ammo or health', run: ([a, n]) => host('give', a ?? '', n ?? '') },
    kill: { help: 'suicide', run: () => host('kill') },
    impulse: { help: '<n>: the impulse a key bound to it sends (9: everything, 100: a FrikBot)', run: ([n]) => { impulse = Math.max(0, Math.min(255, Number(n) || 0)); } },
    bind: {
      help: '<key> [command]: what a key does (+forward, impulse 7, god…)',
      run: ([k, ...cmd]) => {
        if (!k) return 'bind <key> [command]';
        const key = k.toLowerCase();
        if (!cmd.length) return binds.get(key) ? `"${key}" = "${binds.get(key)}"` : `"${key}" is not bound`;
        binds.set(key, cmd.join(' ')); saveBinds();
      },
    },
    unbind: { help: '<key>: unbound', run: ([k]) => { if (!k) return 'unbind <key>'; binds.set(k.toLowerCase(), null); saveBinds(); } },
    unbindall: { help: 'every key unbound (` and Escape still work)', run: () => { binds.unbindAll(); saveBinds(); } },
    bindlist: { help: 'the bindings', run: () => [...binds.map].sort(([a], [b]) => a.localeCompare(b)).map(([k, c]) => `${k.padEnd(10)} "${c}"`).join('\n') },
    resetbinds: { help: "Quake's keys again", run: () => { binds.reset(); saveBinds(); } },
    sql: {
      help: '<statement>: run SQL on the live game',
      run: async (a, c, raw) => {
        if (!raw || !db) return 'sql <statement>';
        const r = /^\s*(select|with|execute\s+block)/i.test(raw) ? await db.query(raw, [], { rowMode: 'object' }) : { rows: [], exec: await db.exec(raw) };
        if (!r.rows.length) return 'OK';
        const cols = Object.keys(r.rows[0]);
        return [cols.join(' '), ...r.rows.slice(0, 20).map((row) => cols.map((k) => fmt(row[k])).join(' ')), ...(r.rows.length > 20 ? [`… ${r.rows.length} rows`] : [])].join('\n');
      },
    },
    viewsize: { help: '<30..120>: (the view always fills the screen here, as viewsize 100)', run: ([v]) => (v === undefined ? '"viewsize" is "100"' : null) },
    gamma: { help: '<0.5..1>: (the palette is drawn as it is)', run: ([v]) => (v === undefined ? '"gamma" is "1"' : null) },
    r_wateralpha: { help: '<0..1>: how see-through water is with Liquids translucent (0.5)', run: alphaCmd('water') },
    r_lavaalpha: { help: '<0..1>: lava (0: as water)', run: alphaCmd('lava') },
    r_slimealpha: { help: '<0..1>: slime (0: as water)', run: alphaCmd('slime') },
    r_telealpha: { help: '<0..1>: teleporters (0: as water)', run: alphaCmd('tele') },
    fov: { help: '<10..170>: the field of view', run: value('fov', async (v) => { settings.fov = Math.max(10, Math.min(170, v)); saveSettings(); await setView(db, viewWidth(), viewHeight() - sbarLines(), settings.fov); }) },
    sensitivity: { help: '<1..11>: the mouse speed', run: value('sensitivity', (v) => { settings.sensitivity = Math.max(1, Math.min(11, v)); saveSettings(); }) },
    volume: { help: '<0..1>: the sound volume', run: (a) => (a.length ? setSfx(Math.round(Math.max(0, Math.min(1, Number(a[0]) || 0)) * 100)) : `"volume" is "${settings.sfx / 100}"`) },
    bgmvolume: { help: '<0..1>: the music volume', run: (a) => (a.length ? setMusicVolume(Math.round(Math.max(0, Math.min(1, Number(a[0]) || 0)) * 100)) : `"bgmvolume" is "${settings.music / 100}"`) },
    logic: { help: '[psql | qc]: the game logic, the PSQL game or the QuakeC VM', run: ([v]) => { if (!v) return `logic is ${settings.logic}`; if (v !== 'psql' && v !== 'qc') return 'logic psql | qc'; setLogic(v); } },
    save: { help: '[0..11 | quick]: save the game in a slot', run: ([s]) => saveGame(slotOf(s)) },
    load: { help: '[0..11 | quick]: load a saved game', run: ([s]) => loadGame(slotOf(s)) },
    pause: { help: 'pause or go on', run: () => { paused = !paused; } },
    toggleconsole: { help: 'bring the console down or put it away', run: () => { con.toggle(); binds.release(); } },
    status: { help: 'the map, the time, the kills and secrets', run: () => (last ? `map ${last.MAP_NAME}, ${last.TIME_.toFixed(1)} s, kills ${last.KILLED}/${last.TOTAL_MONSTERS}, secrets ${last.FOUND_SECRETS}/${last.TOTAL_SECRETS}, ${settings.logic === 'qc' ? 'QuakeC' : 'PSQL'} game` : 'no game running') },
    version: { help: 'what runs this', run: () => `Firebird Quake on ${$('engine').textContent}` },
    quit: { help: 'a new game from the start map', run: () => startMap('start', true) },
  };
  C.cmdlist = C.help;
  return C;
}

function menuActions() {
  const newGame = () => startMap('start', true).catch((err) => setStatus(err.message, true));
  return {
    newGame,
    quit: newGame,
    multiplayer: () => setMode('deathmatch'),
    saves: () => saveSlots,
    save: (slot) => saveGame(slot),
    load: (slot) => loadGame(slot),
    keys: {
      list: BUTTONS,
      keysFor: (cmd) => binds.keysFor(cmd),
      bind: (key, cmd) => { binds.set(key, cmd); saveBinds(); },
      clear: (cmd) => { for (const key of binds.keysFor(cmd)) binds.set(key, null); saveBinds(); },
      keyName,
    },
    options: [
      { label: 'Customize controls', kind: 'keys' },
      { label: 'Reset to defaults', kind: 'action', change: () => { Object.assign(settings, { sensitivity: 3, alwaysRun: true, invertMouse: false }); setSfx(70); setMusicVolume(50); binds.reset(); saveBinds(); } },
      { label: 'Screen size', kind: 'value', get: () => `${viewWidth()}x${viewHeight()}`, change: (d) => { const k = ['low', 'high', 'max']; setDetail(k[(k.indexOf(settings.detail) + (d < 0 ? 2 : 1)) % 3]); } },
      { label: 'Mouse Speed', kind: 'slider', get: () => (settings.sensitivity - 1) / 10, change: (d) => { settings.sensitivity = clamp(settings.sensitivity + d * 0.5, 1, 11); saveSettings(); } },
      { label: 'CD Music Volume', kind: 'slider', get: () => settings.music / 100, change: (d) => setMusicVolume(clamp(settings.music + d * 10, 0, 100)) },
      { label: 'Sound Volume', kind: 'slider', get: () => settings.sfx / 100, change: (d) => setSfx(clamp(settings.sfx + d * 10, 0, 100)) },
      { label: 'Always Run', kind: 'check', get: () => settings.alwaysRun, change: () => { settings.alwaysRun = !settings.alwaysRun; saveSettings(); } },
      { label: 'Invert Mouse', kind: 'check', get: () => settings.invertMouse, change: () => { settings.invertMouse = !settings.invertMouse; saveSettings(); } },
      { label: 'Game', kind: 'value', get: () => ({ single: 'single player', deathmatch: 'deathmatch', coop: 'coop' })[settings.mode], change: (d) => { const m = ['single', 'deathmatch', 'coop']; setMode(m[(m.indexOf(settings.mode) + (d < 0 ? 2 : 1)) % 3]); } },
      { label: 'Bots', kind: 'value', get: () => settings.bots, change: (d) => { settings.bots = clamp(settings.bots + (d < 0 ? -1 : 1), 1, 7); $('bots').value = String(settings.bots); saveSettings(); if (settings.mode !== 'single' && map) startMap(map.name, true).catch((err) => setStatus(err.message, true)); } },
      { label: 'Game logic', kind: 'value', get: () => (settings.logic === 'qc' ? 'QuakeC VM' : 'PSQL'), change: () => setLogic(settings.logic === 'qc' ? 'psql' : 'qc') },
      { label: 'Liquids', kind: 'value', get: () => settings.liquids, change: () => setLiquids(settings.liquids === 'opaque' ? 'translucent' : 'opaque') },
      { label: 'Renderer', kind: 'value', get: () => (settings.renderer === 'sql' ? 'all in SQL' : 'fast'), change: () => setRenderer(settings.renderer === 'sql' ? 'fast' : 'sql') },
    ],
  };
}

$('demo-record').addEventListener('click', () => (recording ? stopRecording() : startRecording()).catch((err) => setStatus(err.message, true)));
$('demo-play').addEventListener('click', () => playDemo());
$('demo-save').addEventListener('click', () => {
  if (!demo) return;
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([JSON.stringify(demo)], { type: 'application/json' }));
  a.download = `${demo.map}.dem.json`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
});
// a save game as a file (saves otherwise live in this browser's IndexedDB), and a file loaded back
$('save-download').addEventListener('click', () => {
  if (!lastSave) return;
  const { name, text } = saveFile(lastSave, pakKey);
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([text], { type: 'application/json' }));
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
});
$('save-file').addEventListener('change', async (e) => {
  const f = e.target.files[0];
  e.target.value = '';
  if (!f || !db || !pak) return;
  try { await loadSave(readSaveFile(await f.text(), pakKey)); } catch (err) { setStatus(sqlMessage(err), true); }
});
$('demo-file').addEventListener('change', async (e) => {
  const f = e.target.files[0];
  if (!f) return;
  try { demo = JSON.parse(await f.text()); await playDemo(demo); } catch (err) { setStatus(`not a demo: ${err.message}`, true); }
});
$('mode').value = settings.mode;
$('mode').addEventListener('change', (e) => setMode(e.target.value));
$('bots').value = String(settings.bots);
$('bots').addEventListener('change', (e) => { settings.bots = Number(e.target.value); saveSettings(); if (settings.mode !== 'single' && map) startMap(map.name, true).catch((err) => setStatus(err.message, true)); });
$('detail').value = settings.detail;
$('detail').addEventListener('change', (e) => setDetail(e.target.value));
$('logic').value = settings.logic;
$('logic').addEventListener('change', (e) => setLogic(e.target.value));
$('liquids').value = settings.liquids;
$('liquids').addEventListener('change', (e) => setLiquids(e.target.value));
$('renderer').value = settings.renderer;
$('renderer').addEventListener('change', (e) => setRenderer(e.target.value));
$('musicvol').value = settings.music;
$('musicvol').addEventListener('input', () => setMusicVolume(Number($('musicvol').value)));
$('skill').value = String(settings.skill);
$('skill').addEventListener('change', (e) => { settings.skill = Number(e.target.value); saveSettings(); });
$('sfxvol').value = settings.sfx;
$('sfxvol').addEventListener('input', () => { settings.sfx = Number($('sfxvol').value); saveSettings(); audio.unlock(); audio.setVolume(settings.sfx / 100);
audio.setMusicVolume(settings.music / 100);
audio.musicMode = settings.musicMode; });

boot();
